import { type FolderTree } from "@mg/schemas/src/commons";
import { ArrowLineLeft, CaretRight, CaretDown } from "@phosphor-icons/react";
import * as Accordion from "@radix-ui/react-accordion";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Button, IconButton, Link, ScrollArea, Text } from "@radix-ui/themes";
import {
  // eslint-disable-next-line import/named
  Link as NavLink,
  useLoaderData,
  useRouter,
  useRouterState,
  useSearch,
} from "@tanstack/react-router";
import cx from "classnames";
import { usePostHog } from "posthog-js/react";
import { useMemo, useRef, useState } from "react";

import { CreateFolderDialog } from "./dialogs/CreateFolderDialog";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "./sidebar/SidebarTooltip";
import { useSidebar } from "./sidebar/useSidebar";

import { useUI } from "../contexts/ui";
import { authLayoutRoute } from "../routes/auth-layout/route";
import { ticketsRoute } from "../routes/tickets/route";
import { errorAnalyticsPayload } from "../utils/analytics";
import { batch } from "../utils/batch";
import { isNil } from "../utils/fp";
import { useAppSelector } from "../utils/hooks";
import { errorMessage } from "../utils/http";
import {
  useEnterprises,
  useSwitchEnterprise,
} from "../utils/queries/enterprises";
import {
  useEditProjectMutation,
  useUpdateFolderMutation,
} from "../utils/queries/projects";

type AppDrawerProps = {
  open: boolean;
  onClose(): void;
};

export function AppDrawer(props: AppDrawerProps) {
  const { open, onClose } = props;
  const posthog = usePostHog();
  const { data: enterprises } = useEnterprises();
  const switchEnterpriseMutation = useSwitchEnterprise();
  const enterpriseId = useAppSelector(
    (state) => state.auth.value?.enterpriseId,
  );
  const userEmail = useAppSelector((state) => state.auth.value?.email);

  const currentEnterprise = enterprises?.find((e) => e._id === enterpriseId);

  return (
    <aside
      className={cx(
        "fixed left-0 z-[4] h-screen w-80 bg-base-white shadow-lg transition-transform",
        {
          "-translate-x-80": !open,
        },
      )}
    >
      <header className="flex items-center justify-between border-b border-b-puntt-neutral-gray-6 p-4">
        <IconButton size="1" variant="soft" color="gray" onClick={onClose}>
          <ArrowLineLeft />
        </IconButton>

        {!enterprises?.length ? null : enterprises?.length === 1 ? (
          <Text size="2" weight="medium" className="ml-2">
            {currentEnterprise?.name}
          </Text>
        ) : (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button
                variant="soft"
                color="gray"
                className="ml-2"
                loading={switchEnterpriseMutation.isPending}
              >
                <Text
                  size="2"
                  weight="medium"
                  className="max-w-[180px] truncate"
                >
                  {currentEnterprise?.name || "Select Enterprise"}
                </Text>
                <CaretDown className="ml-1" />
              </Button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="min-w-[220px] rounded-md bg-base-white p-1 shadow-lg"
                sideOffset={5}
              >
                {enterprises
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((enterprise) => (
                    <DropdownMenu.Item
                      key={enterprise._id}
                      data-id={enterprise._id}
                      className={cx(
                        "flex items-center rounded-sm px-2 py-1.5 outline-none",
                        "hover:bg-puntt-neutral-gray-4 focus:bg-puntt-neutral-gray-4",
                        {
                          "bg-puntt-neutral-gray-2":
                            enterprise._id === enterpriseId,
                          "cursor-pointer": enterprise._id !== enterpriseId,
                        },
                      )}
                      onClick={() => {
                        if (enterprise._id !== enterpriseId && enterprise._id) {
                          const start = performance.now();

                          switchEnterpriseMutation.mutate(enterprise._id, {
                            onSuccess: () => {
                              posthog.capture("switch_enterprise", {
                                from: enterpriseId,
                                to: enterprise._id,
                                duration_ms: performance.now() - start,
                                user: userEmail,
                              });
                              location.reload();
                            },
                          });
                        }
                      }}
                    >
                      <Text size="2">{enterprise.name}</Text>
                    </DropdownMenu.Item>
                  ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </header>

      <FolderTreeView />
    </aside>
  );
}

export function FolderTreeView() {
  const { folderTree = [] } = useLoaderData({ from: authLayoutRoute.id }) ?? {};
  const { matches } = useRouterState();
  const isOnTicketsPage = matches.find(
    (m) => m.pathname.includes("/tickets") || m.pathname.includes("/ticket"),
  );
  const { open } = useSidebar();

  const user = useAppSelector((state) => state.auth.value);

  const [folderId, setFolderId] = useState<string>();
  const sortedFolderTree = useMemo(() => {
    return folderTree.sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [folderTree]);

  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);

  if (!open || isNil(user?.email) || !isOnTicketsPage) return null;

  function handleCreateNestedFolder(folderId: string) {
    setFolderId(folderId);
    setCreateFolderDialogOpen(true);
  }

  function renderTree() {
    if (!sortedFolderTree.length) {
      return (
        <Text align="center" color="gray" mt="5">
          Add Your First Project
        </Text>
      );
    }

    return (
      <div className="flex min-h-0 shrink flex-col px-2">
        <div
          className="relative flex w-full shrink-0 items-center gap-4 rounded-md px-2 py-1.5"
          style={{ background: "inherit" }}
          data-testid="folder-tree-header"
        >
          <Text size="2" weight="bold" className="flex-1 truncate px-0">
            Folders
          </Text>
          <div className="absolute right-3 top-1/2 flex h-full -translate-y-1/2 items-center">
            <Button
              size="1"
              variant="ghost"
              color="gray"
              className="white hover:bg-puntt-neutral-gray-4 hover:shadow-sm"
              style={{ zIndex: 2 }}
              onClick={() => {
                setFolderId(undefined);
                setCreateFolderDialogOpen(true);
              }}
              tabIndex={0}
              aria-label="Add Folder"
            >
              +
            </Button>
          </div>
        </div>
        <ScrollArea
          type="auto"
          scrollbars="vertical"
          className="min-h-0 overflow-x-hidden"
          data-testid="folder-tree-list"
        >
          {sortedFolderTree.map((folder: any) => (
            <Folder
              key={folder._id}
              {...folder}
              onCreate={handleCreateNestedFolder}
            />
          ))}
        </ScrollArea>
      </div>
    );
  }

  return (
    <>
      <CreateFolderDialog
        open={createFolderDialogOpen}
        onOpenChange={setCreateFolderDialogOpen}
        folderId={folderId}
      />
      {renderTree()}
    </>
  );
}

function Folder(props: FolderTree & { onCreate: (folderId: string) => void }) {
  const { _id, name, folders = [], onCreate } = props;
  const [isOver, setIsOver] = useState(false);
  const posthog = usePostHog();
  const { notify } = useUI();
  const updateFolderMutation = useUpdateFolderMutation();
  const editProjectMutation = useEditProjectMutation();
  const router = useRouter();
  const { folderId } = useSearch({ strict: false });
  const elem = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const isActive = folderId === _id;

  const onMoveToFolder = async (
    draggedIds: { _id: string; isFolder: boolean }[],
    targetFolderId: string,
  ) => {
    const startTime = performance.now();

    try {
      await batch(
        draggedIds,
        1,
        async ([dragTargetId]) => {
          if (dragTargetId.isFolder) {
            await updateFolderMutation.mutateAsync({
              _id: dragTargetId._id,
              parentFolder: targetFolderId,
            });
          } else {
            await editProjectMutation.mutateAsync({
              _id: dragTargetId._id,
              folder: targetFolderId,
            });
          }
        },
        5,
      );
      router.invalidate();
      notify({
        title: "Moved to folder",
        message: `${draggedIds.length} item${draggedIds.length === 1 ? "" : "s"} moved to ${name}`,
        variant: "success",
      });
      posthog.capture("tickets_dragged_to_sidebar", {
        folder_id: targetFolderId,
        dropped_ids: draggedIds,
        duration_ms: performance.now() - startTime,
        num_dropped: draggedIds.length,
      });
    } catch (error) {
      const message = await errorMessage(error);
      notify({
        title: "Error dragging to folder",
        message,
        variant: "error",
      });
      posthog.capture("tickets_dragged_to_sidebar_error", {
        folder_id: targetFolderId,
        num_dropped: draggedIds.length,
        dropped_ids: draggedIds,
        duration_ms: performance.now() - startTime,
        ...errorAnalyticsPayload(error),
      });
    }

    router.invalidate();
  };

  function getDragData<T>(e: React.DragEvent<T>) {
    try {
      const parsed = JSON.parse(
        e.dataTransfer?.getData("application/json") ?? "",
      ) as {
        _id: string;
        isFolder: boolean;
      }[];
      if (isNil(parsed)) return;

      // Prevent making a folder its own parent
      const isDropOnSelf = parsed.some(
        (item: { _id: string }) => item._id === _id,
      );
      if (isDropOnSelf) {
        console.warn("Cannot drop a folder onto itself");
        return;
      }

      // Prevent circular folder nesting
      const hasInvalidNesting = parsed.some((item: any) => {
        if (!item.isFolder) return false;

        // Find if the drop target is inside the dragged folder
        const draggedFolderSelector = `[data-folder-id="${item._id}"]`;
        return !isNil(elem.current?.closest(draggedFolderSelector));
      });

      if (hasInvalidNesting) {
        console.warn("Cannot move a folder into one of its sub-folders");
        return;
      }

      return parsed;
    } catch (err) {
      console.error("Invalid drag data", err);
      return null;
    }
  }

  function handleMouseEnter() {
    if (nameRef.current) {
      setShowTooltip(nameRef.current.scrollWidth > nameRef.current.clientWidth);
    }
  }

  function handleMouseLeave() {
    setShowTooltip(false);
  }

  return (
    <Accordion.Root
      ref={elem}
      type="single"
      collapsible
      data-folder-id={_id}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "move";
        setIsOver(true);
      }}
      onDragLeave={() => {
        setIsOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOver(false);

        const parsed = getDragData(e);
        if (isNil(parsed)) return;

        onMoveToFolder(parsed, _id!);
      }}
    >
      <Accordion.Item value={_id as string}>
        <Accordion.Header
          className={cx(
            "group/folder-item relative flex w-full items-center gap-2 rounded-md px-2 py-1.5 hover:bg-puntt-neutral-gray-3",
            {
              "rounded-lg bg-puntt-accent-10 text-base-white": isOver,
              "bg-puntt-neutral-gray-3 hover:bg-puntt-neutral-gray-4": isActive,
            },
          )}
        >
          <Accordion.Trigger
            asChild
            className={cx("group", {
              "w-0 overflow-hidden opacity-0": !folders.length,
            })}
          >
            <IconButton size="1" variant="ghost">
              <CaretRight
                className="transition-transform group-data-[state=open]:rotate-90"
                color="rgb(var(--puntt-neutral-gray-11))"
              />
            </IconButton>
          </Accordion.Trigger>
          <Tooltip open={showTooltip} delayDuration={200}>
            <TooltipTrigger asChild>
              <Link
                asChild
                className={cx(
                  "puntt-link w-full flex-1 overflow-hidden text-ellipsis whitespace-nowrap",
                  {
                    "!text-base-white": isOver,
                  },
                )}
                size="2"
                weight="medium"
                draggable="true"
                onDragStart={(e) => {
                  e.stopPropagation();
                  // Set data for folder being dragged
                  const dragData = JSON.stringify([{ _id, isFolder: true }]);
                  e.dataTransfer.setData("application/json", dragData);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <NavLink
                  to={ticketsRoute.to}
                  search={(prev) => ({ ...prev, folderId: _id })}
                  className="w-full overflow-hidden"
                >
                  <span ref={nameRef} className="block w-full truncate">
                    {name}
                  </span>
                </NavLink>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8} align="start">
              {name}
            </TooltipContent>
          </Tooltip>
          <div className="absolute right-3 top-1/2 flex h-full -translate-y-1/2 items-center">
            <div
              className="pointer-events-none -mr-2 hidden h-6 w-12 group-hover/folder-item:block"
              style={{
                background:
                  "linear-gradient(to left, rgb(var(--puntt-cool-gray-3)) 25%, transparent 100%)",
                zIndex: 1,
              }}
            />
            <Button
              size="1"
              variant="ghost"
              color="gray"
              className="hidden bg-puntt-neutral-gray-3 shadow-sm hover:bg-puntt-neutral-gray-4 group-hover/folder-item:flex"
              style={{ zIndex: 2 }}
              onClick={() => onCreate(_id!)}
            >
              +
            </Button>
          </div>
        </Accordion.Header>

        <Accordion.Content className="ml-4">
          {folders.map((folder) => (
            <Folder key={folder._id} {...folder} onCreate={onCreate} />
          ))}
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
}
